import { useEffect, useRef, useState } from 'react'

import {
  collectDealEvidence,
  getDealEvidence,
  getDealEvidenceCollection,
  reviewDealEvidence,
} from '@/lib/deal-evidence-api'

const ACTIVE_COLLECTION_STATES = new Set(['queued', 'running', 'waiting_retry', 'waiting_provider'])

function intentKey(prefix) {
  return `${prefix}-${crypto.randomUUID()}`
}

function wait(milliseconds) {
  return new Promise((resolve) => { window.setTimeout(resolve, milliseconds) })
}

export function useDealEvidence({ dealId, csrfToken, pollDelayMs = 1_000 }) {
  const generationRef = useRef(0)
  const readSequenceRef = useRef(0)
  const refreshPromiseRef = useRef(null)
  const [viewState, setViewState] = useState({
    conflict: null,
    dealId: null,
    error: null,
    evidence: null,
    loaded: false,
  })
  const [refreshingDealId, setRefreshingDealId] = useState(null)
  const currentView = viewState.dealId === dealId ? viewState : null
  const evidence = currentView?.evidence ?? null
  const isLoading = Boolean(dealId) && currentView?.loaded !== true
  const isRefreshing = refreshingDealId === dealId
  const error = currentView?.error ?? null
  const conflict = currentView?.conflict ?? null

  const updateCurrentView = (updates) => {
    setViewState((current) => ({
      conflict: current.dealId === dealId ? current.conflict : null,
      dealId,
      error: current.dealId === dealId ? current.error : null,
      evidence: current.dealId === dealId ? current.evidence : null,
      loaded: current.dealId === dealId && current.loaded,
      ...updates,
    }))
  }

  const loadCurrent = async (generation = generationRef.current) => {
    if (!dealId) return null

    const readSequence = readSequenceRef.current + 1
    readSequenceRef.current = readSequence
    const payload = await getDealEvidence(dealId, { csrfToken })
    if (
      generation !== generationRef.current ||
      readSequence !== readSequenceRef.current ||
      payload?.evidence?.dealId !== dealId
    ) return null

    updateCurrentView({ evidence: payload.evidence, error: null, loaded: true })
    return payload.evidence
  }

  useEffect(() => {
    generationRef.current += 1
    const generation = generationRef.current
    const readSequence = readSequenceRef.current + 1
    readSequenceRef.current = readSequence
    refreshPromiseRef.current = null

    if (!dealId) return undefined

    let ignore = false
    getDealEvidence(dealId, { csrfToken })
      .then((payload) => {
        if (
          ignore || generation !== generationRef.current ||
          readSequence !== readSequenceRef.current || payload?.evidence?.dealId !== dealId
        ) return
        setViewState({ conflict: null, dealId, error: null, evidence: payload.evidence, loaded: true })
      })
      .catch((loadError) => {
        if (!ignore && generation === generationRef.current) {
          setViewState({ conflict: null, dealId, error: loadError.message, evidence: null, loaded: true })
        }
      })

    return () => {
      ignore = true
      generationRef.current += 1
      refreshPromiseRef.current = null
    }
  }, [csrfToken, dealId])

  const refresh = () => {
    if (!dealId) return Promise.resolve(null)
    if (refreshPromiseRef.current) return refreshPromiseRef.current

    const generation = generationRef.current
    const request = {
      idempotencyKey: intentKey('evidence-refresh'),
      mode: 'refresh',
      expectedContextRevision: evidence?.context?.revision ?? 0,
      profileIds: (evidence?.context?.profiles ?? []).map((profile) => profile.id),
    }
    setRefreshingDealId(dealId)
    updateCurrentView({ conflict: null, error: null })

    const promise = (async () => {
      let payload = await collectDealEvidence(dealId, request, { csrfToken })
      let collection = payload.collection
      while (collection && ACTIVE_COLLECTION_STATES.has(collection.status)) {
        await wait(pollDelayMs)
        if (generation !== generationRef.current) return null
        payload = await getDealEvidenceCollection(dealId, collection.id, { csrfToken })
        collection = payload.collection
        if (collection?.contextChanged) return null
      }
      if (generation !== generationRef.current) return null
      return loadCurrent(generation)
    })()
      .catch((refreshError) => {
        if (generation === generationRef.current) updateCurrentView({ error: refreshError.message })
        throw refreshError
      })
      .finally(() => {
        if (refreshPromiseRef.current === promise) refreshPromiseRef.current = null
        if (generation === generationRef.current) setRefreshingDealId(null)
      })

    refreshPromiseRef.current = promise
    return promise
  }

  const review = async (request) => {
    const generation = generationRef.current
    updateCurrentView({ conflict: null, error: null })
    try {
      const payload = await reviewDealEvidence(dealId, request, { csrfToken })
      await loadCurrent(generation)
      return payload
    } catch (reviewError) {
      if (reviewError.status === 409 && generation === generationRef.current) {
        updateCurrentView({ conflict: reviewError.message })
        await loadCurrent(generation)
      } else if (generation === generationRef.current) {
        updateCurrentView({ error: reviewError.message })
      }
      throw reviewError
    }
  }

  const clearConflict = () => { updateCurrentView({ conflict: null }) }

  return { clearConflict, conflict, error, evidence, isLoading, isRefreshing, refresh, review, reload: loadCurrent }
}
